import { useEffect } from "react";
import { school } from "@/data/schoolData";

function upsertMeta(name, content, attribute = "name") {
  if (!content) return;
  let element = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

export function SEO({ title, description, path = "", faqItems = [], breadcrumbs = [] }) {
  useEffect(() => {
    const brandSuffix = ` | ${school.name}`;
    const hasBrand = title === school.name || title.includes(school.name);
    const pageTitle = title ? (hasBrand ? title : `${title}${brandSuffix}`) : school.seo.defaultTitle;
    const pageDescription = description || school.seo.defaultDescription;
    const canonical = `${window.location.origin}${path || window.location.pathname}`;
    document.title = pageTitle;
    upsertMeta("description", pageDescription);
    upsertMeta("og:title", pageTitle, "property");
    upsertMeta("og:description", pageDescription, "property");
    upsertMeta("og:type", "website", "property");
    upsertMeta("og:url", canonical, "property");
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", pageTitle);
    upsertMeta("twitter:description", pageDescription);

    const graph = [
      {
        "@type": "SportsActivityLocation",
        "@id": `${window.location.origin}/#school`,
        name: school.name,
        description: school.positioning,
        url: window.location.origin,
        telephone: school.phone,
        email: school.email,
        address: {
          "@type": "PostalAddress",
          streetAddress: school.address,
          addressLocality: school.city,
          addressRegion: school.stateCode,
          postalCode: school.zip,
          addressCountry: "US",
        },
        areaServed: school.city,
        sameAs: school.socialLinks.map((link) => link.href),
      },
      {
        "@type": "Organization",
        name: school.name,
        url: window.location.origin,
        logo: school.branding?.logoUrl || school.images.belt,
      },
    ];
    if (breadcrumbs.length) {
      graph.push({
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: crumb.name,
          item: `${window.location.origin}${crumb.path}`,
        })),
      });
    }
    if (faqItems.length) {
      graph.push({
        "@type": "FAQPage",
        mainEntity: faqItems.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      });
    }
    let script = document.getElementById("jma-structured-data");
    if (!script) {
      script = document.createElement("script");
      script.id = "jma-structured-data";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    let canonicalLink = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical;
  }, [title, description, path, faqItems, breadcrumbs]);

  return null;
}
